import { Link, useMatches } from '@remix-run/react'
import { Home } from 'lucide-react'
import { Fragment } from 'react'
import { useIsMobile } from '#app/hooks/use-mobile.tsx'
import {
	type BreadcrumbHandle,
	type BreadcrumbItem,
} from '#app/utils/breadcrumb.tsx'

const getImage = (item: BreadcrumbItem) => {
	if (item.imageUrl) {
		return (
			<img
				src={item.imageUrl}
				alt={item.label}
				className="h-4 w-4 shrink-0 rounded-full"
			/>
		)
	}
	return null
}

export function HeaderBreadcrumb() {
	const matches = useMatches()
	const isMobile = useIsMobile()

	const breadcrumbs = matches
		.filter((match): match is typeof match & { handle: BreadcrumbHandle } =>
			Boolean((match.handle as BreadcrumbHandle | undefined)?.breadcrumb),
		)
		.flatMap((match) => {
			const { breadcrumb } = match.handle
			if (typeof breadcrumb === 'function') {
				const result = breadcrumb(match)
				return result ? (Array.isArray(result) ? result : [result]) : []
			}
			return Array.isArray(breadcrumb) ? breadcrumb : [breadcrumb]
		})
		.filter((item): item is BreadcrumbItem => Boolean(item))

	if (breadcrumbs.length === 0) return null

	return (
		<nav
			aria-label="Breadcrumb"
			className="flex min-w-0 items-center gap-2 overflow-hidden whitespace-nowrap text-body-2xs text-ink-fg-soft"
		>
			<Link
				to="/"
				aria-label="Home"
				className="flex shrink-0 items-center transition-colors hover:text-ink-fg"
			>
				<Home className="h-4 w-4" />
			</Link>
			{breadcrumbs
				.slice(
					isMobile ? (breadcrumbs.length > 3 ? breadcrumbs.length - 3 : 0) : 0,
				)
				.map((item, i, arr) => {
					const isLast = i === arr.length - 1
					return (
						<Fragment key={i}>
							<span aria-hidden className="shrink-0 text-ink-fg-soft">
								/
							</span>
							{isLast ? (
								<span
									aria-current="page"
									className="flex min-w-0 items-center gap-1.5 font-medium text-ink-fg"
								>
									{getImage(item)}
									<span className="truncate">{item.label}</span>
								</span>
							) : item.href ? (
								<Link
									to={item.href}
									className="flex min-w-0 items-center gap-1.5 transition-colors hover:text-ink-fg"
								>
									{getImage(item)}
									<span className="truncate">{item.label}</span>
								</Link>
							) : (
								<span className="flex min-w-0 items-center gap-1.5 font-medium text-ink-fg">
									{getImage(item)}
									<span className="truncate">{item.label}</span>
								</span>
							)}
						</Fragment>
					)
				})}
		</nav>
	)
}
